import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // ─── Main Layout Routes (Discover is default landing at / and /discover) ───
    {
      path: '/',
      name: 'home',
      meta: {
        layout: 'main',
        fullBleed: true,
        seo: {
          titleKey: 'seo.home_title',
          descriptionKey: 'seo.home_description',
        },
      },
      component: () => import('@/views/discover/DiscoverView.vue'),
    },
    {
      path: '/discover',
      name: 'discover',
      meta: {
        layout: 'main',
        fullBleed: true,
        seo: {
          titleKey: 'seo.discover_title',
          descriptionKey: 'seo.discover_description',
        },
      },
      component: () => import('@/views/discover/DiscoverView.vue'),
    },
    {
      path: '/locations/new',
      name: 'add-location',
      meta: {
        layout: 'main',
        requiresAuth: true,
        seo: { titleKey: 'seo.add_location_title', noIndex: true },
      },
      component: () => import('@/views/locations/AddLocationView.vue'),
    },
    {
      path: '/locations/:id',
      name: 'location-detail',
      meta: { layout: 'main', seoManaged: true },
      component: () => import('@/views/locations/LocationDetailView.vue'),
    },
    {
      path: '/sessions',
      name: 'sessions-list',
      meta: {
        layout: 'main',
        seo: {
          titleKey: 'seo.sessions_title',
          descriptionKey: 'seo.sessions_description',
        },
      },
      component: () => import('@/views/sessions/SessionsListView.vue'),
    },
    {
      path: '/sessions/new',
      name: 'add-session',
      meta: {
        layout: 'main',
        requiresAuth: true,
        seo: { titleKey: 'seo.add_session_title', noIndex: true },
      },
      component: () => import('@/views/sessions/AddSessionView.vue'),
    },
    {
      path: '/sessions/:id',
      name: 'session-detail',
      meta: { layout: 'main', seoManaged: true },
      component: () => import('@/views/sessions/SessionDetailView.vue'),
    },
    {
      path: '/speakers',
      name: 'speakers',
      meta: {
        layout: 'main',
        seo: {
          titleKey: 'seo.speakers_title',
          descriptionKey: 'seo.speakers_description',
        },
      },
      component: () => import('@/views/speakers/SpeakersListView.vue'),
    },
    {
      path: '/speakers/request',
      name: 'speaker-request',
      meta: {
        layout: 'main',
        requiresAuth: true,
        seo: { titleKey: 'seo.speaker_request_title', noIndex: true },
      },
      component: () => import('@/views/speakers/SpeakerRequestView.vue'),
    },
    {
      path: '/speakers/:id',
      name: 'speaker-detail',
      meta: { layout: 'main', seoManaged: true },
      component: () => import('@/views/speakers/SpeakerDetailView.vue'),
    },
    {
      path: '/profile',
      name: 'profile',
      meta: {
        layout: 'main',
        requiresAuth: true,
        seo: { titleKey: 'seo.profile_title', noIndex: true },
      },
      component: () => import('@/views/profile/ProfileView.vue'),
    },
    {
      path: '/profile/favorites',
      name: 'profile-favorites',
      meta: {
        layout: 'main',
        requiresAuth: true,
        seo: { titleKey: 'seo.favorites_title', noIndex: true },
      },
      component: () => import('@/views/profile/FavoritesView.vue'),
    },
    {
      path: '/profile/prayer-notifications',
      name: 'prayer-notifications',
      meta: {
        layout: 'main',
        seo: { titleKey: 'seo.prayer_notifications_title', noIndex: true },
      },
      component: () => import('@/views/profile/PrayerNotificationsView.vue'),
    },

    // ─── Short link resolver (share links) ───
    {
      path: '/s/:shortCode',
      name: 'share-resolve',
      meta: { layout: 'main', seo: { noIndex: true } },
      component: () => import('@/views/ShareResolveView.vue'),
    },

    // ─── Auth Layout Routes (no FAB / bottom nav) ───
    {
      path: '/login',
      name: 'login',
      meta: {
        layout: 'auth',
        seo: { titleKey: 'seo.login_title', noIndex: true },
      },
      component: () => import('@/views/auth/LoginView.vue'),
    },
    {
      path: '/register',
      name: 'register',
      meta: {
        layout: 'auth',
        seo: { titleKey: 'seo.register_title', noIndex: true },
      },
      component: () => import('@/views/auth/RegisterView.vue'),
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      meta: {
        layout: 'auth',
        seo: { titleKey: 'seo.forgot_password_title', noIndex: true },
      },
      component: () => import('@/views/auth/ForgotPasswordView.vue'),
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      meta: {
        layout: 'auth',
        seo: { titleKey: 'seo.reset_password_title', noIndex: true },
      },
      component: () => import('@/views/auth/ResetPasswordView.vue'),
    },

    // ─── 404 catch-all (must stay last) ───
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      meta: {
        layout: 'main',
        seo: { titleKey: 'seo.not_found_title', noIndex: true },
      },
      component: () => import('@/views/discover/DiscoverView.vue'),
    },
  ],
})

router.beforeEach((to, _from, next) => {
  const auth = useAuthStore()
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    next({ path: '/login', query: { redirect: to.fullPath } })
  } else {
    next()
  }
})

export default router
